/**
 * Admin Standards Page
 *
 * Lists all official standards with reactive updates.
 * Provides upload dialog for adding new standards.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { ShieldCheck, Upload } from "lucide-react";
import { api } from "convex/_generated/api";
import { Button } from "@/components/ui/primitives";
import { StateCard } from "@/components/ui";
import { StandardCard } from "./StandardCard";
import { UploadStandardDialog } from "./UploadStandardDialog";

export function StandardsPage() {
  const { data: standards, isLoading } = useQuery(
    convexQuery(api.admin_standards_queries.list, {}),
  );

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const isEmpty = !isLoading && standards && standards.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={20} className="text-accent" />
          <h1 className="text-[18px] font-semibold text-primary">
            Official Standards
          </h1>
        </div>
        <div className="flex-1" />
        <Button
          variant="primary"
          icon={Upload}
          onClick={() => setUploadDialogOpen(true)}
        >
          Upload Standard
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="loading">
            <StateCard.Icon />
            <StateCard.Title>Loading standards...</StateCard.Title>
            <StateCard.Description>
              Fetching official standards from the database.
            </StateCard.Description>
          </StateCard>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex items-center justify-center py-16">
          <StateCard variant="empty">
            <StateCard.Icon />
            <StateCard.Title>No official standards yet</StateCard.Title>
            <StateCard.Description>
              Upload a standard document to create pre-built checklists for your
              auditors.
            </StateCard.Description>
            <StateCard.Action>
              <button
                type="button"
                onClick={() => setUploadDialogOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
              >
                <Upload size={14} />
                Upload Your First Standard
              </button>
            </StateCard.Action>
          </StateCard>
        </div>
      )}

      {/* Standards list */}
      {!isLoading && !isEmpty && standards && (
        <div className="space-y-3">
          {standards.map((standard) => (
            <StandardCard key={standard._id} standard={standard} />
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <UploadStandardDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
      />
    </div>
  );
}
